import React, { useState, useEffect } from "react";
import "./checkStock.css";

const API_BASE_URL = process.env.NODE_ENV === 'development' 
    ? 'http://127.0.0.1:8080'
    : 'https://bymolive-backend-995926394015.us-central1.run.app';

const RawCosts = ({ inventory, variantSales }) => {
    const [searchTerm, setSearchTerm] = useState("");
    const [filteredProducts, setFilteredProducts] = useState([]);
    const [sortConfig, setSortConfig] = useState({
        key: null,
        direction: 'ascending'
    });
    const [productCosts, setProductCosts] = useState({});
    const [editedCosts, setEditedCosts] = useState({});
    const [savingCosts, setSavingCosts] = useState({});
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState("");

    // Fetch costs data when component mounts
    useEffect(() => {
        fetchCosts();
    }, []);

    const fetchCosts = async () => {
        try {
            setLoading(true);
            const response = await fetch(`${API_BASE_URL}/api/costs`);
            
            if (!response.ok) {
                throw new Error(`HTTP error! Status: ${response.status}`);
            }
            
            const data = await response.json();
            setProductCosts(data.costs || {});
        } catch (error) {
            console.error("Error fetching costs:", error);
            setError("Failed to load product costs");
        } finally {
            setLoading(false);
        }
    };

    const handleCostChange = (productId, value) => {
        setEditedCosts(prev => ({
            ...prev,
            [productId]: value
        }));
    };

    const saveCost = async (product) => {
        const productId = product.product_id;
        const costValue = editedCosts[productId];
        
        if (!costValue) return;
        
        try {
            setSavingCosts(prev => ({
                ...prev,
                [productId]: true
            }));
            
            const response = await fetch(`${API_BASE_URL}/api/costs/${productId}`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify({
                    cost: costValue,
                    product_title: product.product_title,
                    sku: product.sku
                })
            });
            
            if (!response.ok) {
                throw new Error(`HTTP error! Status: ${response.status}`);
            }
            
            // Update the productCosts state with the new value
            setProductCosts(prev => ({
                ...prev,
                [productId]: {
                    cost: costValue,
                    product_title: product.product_title,
                    sku: product.sku
                }
            }));
            
            // Clear the edited state for this product
            setEditedCosts(prev => {
                const newState = { ...prev };
                delete newState[productId];
                return newState;
            });
            
        } catch (error) {
            console.error(`Error saving cost for product ${productId}:`, error);
            setError(`Failed to save cost for ${product.product_title}`);
        } finally {
            setSavingCosts(prev => {
                const newState = { ...prev };
                delete newState[productId];
                return newState;
            });
        }
    };

    // Process inventory data when it changes or search term changes
    useEffect(() => {
        if (!inventory || inventory.length === 0) {
            setFilteredProducts([]);
            return;
        }

        // Process inventory to aggregate variant data
        const processedProducts = inventory.map(product => {
            // Calculate total stock across all variants
            const totalStock = product.variants.reduce((sum, variant) => 
                sum + (variant.inventory_quantity || 0), 0);
            
            // Calculate total sold using variantSales data
            const totalSold = product.variants.reduce((sum, variant) => {
                const salesKey = `${product.product_id}-${variant.id}`;
                return sum + (variantSales?.[salesKey] || 0);
            }, 0);
            
            // Get the price and compare_at_price from the first variant
            const firstVariant = product.variants[0] || {};
            const price = firstVariant.price || "N/A";
            const compareAtPrice = firstVariant.compare_at_price || null;
            const sku = firstVariant.sku || "No SKU";
            
            return {
                ...product,
                totalStock,
                totalSold,
                price,
                compareAtPrice,
                sku
            };
        });

        // Filter products based on search term
        let filtered = processedProducts;
        if (searchTerm) {
            const lowerCaseSearch = searchTerm.toLowerCase();
            filtered = processedProducts.filter(product => 
                product.product_title.toLowerCase().includes(lowerCaseSearch) ||
                product.variants.some(variant => 
                    (variant.sku && variant.sku.toLowerCase().includes(lowerCaseSearch)) ||
                    (variant.barcode && variant.barcode.toLowerCase().includes(lowerCaseSearch))
                )
            );
        }

        // Sort the filtered products if sort config is set
        if (sortConfig.key) {
            filtered.sort((a, b) => {
                if (a[sortConfig.key] < b[sortConfig.key]) {
                    return sortConfig.direction === 'ascending' ? -1 : 1;
                }
                if (a[sortConfig.key] > b[sortConfig.key]) {
                    return sortConfig.direction === 'ascending' ? 1 : -1;
                }
                return 0;
            });
        }

        setFilteredProducts(filtered);
    }, [inventory, searchTerm, sortConfig, variantSales]);

    const handleSort = (key) => {
        let direction = 'ascending';
        if (sortConfig.key === key && sortConfig.direction === 'ascending') {
            direction = 'descending';
        }
        setSortConfig({ key, direction });
    };

    const formatPrice = (price) => {
        return `${parseFloat(price).toFixed(2)} RON`;
    };

    return (
        <div className="raw-costs-container">
            <div className="search-container">
                <input
                    type="text"
                    placeholder="Search by product name, SKU, or barcode..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    className="search-input"
                />
            </div>

            {error && <div className="error-message">{error}</div>}

            {filteredProducts.length === 0 ? (
                <div className="no-results">
                    {inventory.length === 0 ? 
                        "No inventory data available. Please fetch inventory first." : 
                        "No products match your search criteria."}
                </div>
            ) : (
                <div className="products-grid">
                    <div className="products-header">
                        <div className="header-cell image-cell">Image</div>
                        <div 
                            className="header-cell name-cell sortable"
                            onClick={() => handleSort('product_title')}
                        >
                            Product Name
                            {sortConfig.key === 'product_title' && (
                                <span className="sort-indicator">
                                    {sortConfig.direction === 'ascending' ? ' ▲' : ' ▼'}
                                </span>
                            )}
                        </div>
                        <div 
                            className="header-cell price-cell sortable"
                            onClick={() => handleSort('price')}
                        >
                            Price
                            {sortConfig.key === 'price' && (
                                <span className="sort-indicator">
                                    {sortConfig.direction === 'ascending' ? ' ▲' : ' ▼'}
                                </span>
                            )}
                        </div>
                        <div 
                            className="header-cell cost-cell"
                        >
                            Cost
                        </div>
                        <div 
                            className="header-cell stock-cell sortable"
                            onClick={() => handleSort('totalStock')}
                        >
                            Stock
                            {sortConfig.key === 'totalStock' && (
                                <span className="sort-indicator">
                                    {sortConfig.direction === 'ascending' ? ' ▲' : ' ▼'}
                                </span>
                            )}
                        </div>
                        <div 
                            className="header-cell sold-cell sortable"
                            onClick={() => handleSort('totalSold')}
                        >
                            Total Sold
                            {sortConfig.key === 'totalSold' && (
                                <span className="sort-indicator">
                                    {sortConfig.direction === 'ascending' ? ' ▲' : ' ▼'}
                                </span>
                            )}
                        </div>
                    </div>

                    {filteredProducts.map((product) => {
                        const hasDiscount = product.compareAtPrice && 
                            parseFloat(product.compareAtPrice) > parseFloat(product.price);
                        
                        const productId = product.product_id;
                        const savedCost = productCosts[productId]?.cost || '';
                        const isEdited = editedCosts[productId] !== undefined;
                        const displayCost = isEdited ? editedCosts[productId] : savedCost;
                        const isSaving = savingCosts[productId];
                        
                        return (
                            <div key={product.product_id} className="product-row">
                                <div className="product-cell image-cell">
                                    {product.image_url ? (
                                        <img 
                                            src={product.image_url} 
                                            alt={product.product_title} 
                                            className="product-image"
                                            style={{
                                                width: '50px',
                                                height: '50px',
                                                objectFit: 'contain'
                                            }}
                                        />
                                    ) : (
                                        <div className="no-image">No Image</div>
                                    )}
                                </div>
                                <div className="product-cell name-cell">
                                    <div className="product-info">
                                        <div className="product-title">
                                            <a 
                                                href={`https://bymo.ro/admin/products/${product.product_id}`} 
                                                target="_blank" 
                                                rel="noopener noreferrer"
                                                className="product-link"
                                            >
                                                {product.product_title}
                                            </a>
                                        </div>
                                        <div className="product-sku">
                                            SKU: {product.sku}
                                        </div>
                                    </div>
                                </div>
                                <div className="product-cell price-cell">
                                    {hasDiscount ? (
                                        <div className="price-info">
                                            <span className="compare-price">{formatPrice(product.compareAtPrice)}</span>
                                            <br />
                                            <span className="current-price">{formatPrice(product.price)}</span>
                                        </div>
                                    ) : (
                                        <div className="price-info">
                                            <span className="current-price">{formatPrice(product.price)}</span>
                                        </div>
                                    )}
                                </div>
                                <div className="product-cell cost-cell">
                                    <div className="cost-input-container">
                                        <input
                                            type="number"
                                            step="0.01"
                                            value={displayCost}
                                            onChange={(e) => handleCostChange(productId, e.target.value)}
                                            className="cost-input"
                                            placeholder="Enter cost..."
                                        />
                                        {isEdited && (
                                            <button 
                                                className="save-cost-button"
                                                onClick={() => saveCost(product)}
                                                disabled={isSaving}
                                            >
                                                {isSaving ? "Saving..." : "Save"}
                                            </button>
                                        )}
                                    </div>
                                </div>
                                <div className="product-cell stock-cell">
                                    {product.totalStock}
                                </div>
                                <div className="product-cell sold-cell">
                                    {product.totalSold || 0}
                                </div>
                            </div>
                        );
                    })}
                </div>
            )}
        </div>
    );
};

export default RawCosts; 